import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Users, Plus, Pencil, Trash2, ShieldCheck, Wrench, TrendingUp, UserX, UserCheck } from 'lucide-react'
import { benutzer as benutzerApi } from '../../lib/api'
import { useAuthStore } from '../../store/authStore'
import { TopBar } from '../../components/layout/TopBar'
import { Card } from '../../components/ui/Card'
import { Button } from '../../components/ui/Button'
import { Input } from '../../components/ui/Input'
import { Select } from '../../components/ui/Select'
import { Modal } from '../../components/ui/Modal'
import { LoadingSpinner } from '../../components/ui/EmptyState'
import { formatDatum } from '../../lib/utils'
import type { Benutzer } from '../../types'

const ROLLE_CONFIG = {
  admin:     { label: 'Admin',      icon: ShieldCheck, bg: 'bg-red-50',     color: 'text-red-600' },
  alles:     { label: 'Vollzugriff',icon: ShieldCheck, bg: 'bg-indigo-50',  color: 'text-indigo-600' },
  techniker: { label: 'Techniker',  icon: Wrench,      bg: 'bg-blue-50',    color: 'text-blue-600' },
  verkauf:   { label: 'Verkauf',    icon: TrendingUp,  bg: 'bg-green-50',   color: 'text-green-600' },
}

interface FormState {
  name: string
  email: string
  rolle: string
  passwort: string
  aktiv: boolean
}

const EMPTY_FORM: FormState = { name: '', email: '', rolle: 'techniker', passwort: '', aktiv: true }

export function BenutzerPage() {
  const qc = useQueryClient()
  const currentUser = useAuthStore((s) => s.user)

  const [editUser, setEditUser] = useState<Benutzer | null>(null)
  const [showNew, setShowNew] = useState(false)
  const [form, setForm] = useState<FormState>(EMPTY_FORM)
  const [deleteConfirm, setDeleteConfirm] = useState<Benutzer | null>(null)

  const { data, isLoading } = useQuery({
    queryKey: ['benutzer'],
    queryFn: () => benutzerApi.list(),
  })

  const createMutation = useMutation({
    mutationFn: (d: FormState) =>
      benutzerApi.create({ name: d.name, email: d.email, passwort: d.passwort, rolle: d.rolle }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['benutzer'] }); setShowNew(false) },
  })

  const updateMutation = useMutation({
    mutationFn: (d: FormState & { id: string }) =>
      benutzerApi.update(d.id, {
        name: d.name, email: d.email, rolle: d.rolle as Benutzer['rolle'],
        aktiv: d.aktiv ? 1 : 0,
        ...(d.passwort ? { passwort: d.passwort } : {}),
      }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['benutzer'] }); setEditUser(null) },
  })

  const deleteMutation = useMutation({
    mutationFn: (id: string) => benutzerApi.delete(id),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['benutzer'] }); setDeleteConfirm(null) },
  })

  const toggleAktiv = (u: Benutzer) =>
    benutzerApi.update(u.id, { name: u.name, email: u.email, rolle: u.rolle, aktiv: u.aktiv ? 0 : 1 })
      .then(() => qc.invalidateQueries({ queryKey: ['benutzer'] }))

  function openEdit(u: Benutzer) {
    setEditUser(u)
    setForm({ name: u.name, email: u.email, rolle: u.rolle, passwort: '', aktiv: !!u.aktiv })
  }

  const set = (k: keyof FormState, v: unknown) => setForm((f) => ({ ...f, [k]: v }))

  const isValid = form.name.trim() && form.email.trim()
  const isNewValid = isValid && form.passwort.length >= 6

  return (
    <>
      <TopBar
        title="Benutzer"
        backTo="/einstellungen"
        rightAction={
          <Button size="sm" icon={<Plus size={16} />} onClick={() => { setForm(EMPTY_FORM); setShowNew(true) }}>
            Neu
          </Button>
        }
      />

      <div className="px-4 py-3 space-y-3 pb-8">
        {isLoading ? <LoadingSpinner /> : (
          <>
            <p className="text-xs text-gray-500 px-1">{data?.length ?? 0} Benutzer</p>
            <div className="space-y-2">
              {(data ?? []).map((u) => {
                const rc = ROLLE_CONFIG[u.rolle] ?? ROLLE_CONFIG.techniker
                const isSelf = currentUser?.id === u.id
                return (
                  <Card key={u.id} className="flex items-center gap-3">
                    {/* Avatar */}
                    <div className={`w-11 h-11 rounded-2xl flex items-center justify-center shrink-0 ${rc.bg} ${u.aktiv ? '' : 'opacity-40'}`}>
                      <rc.icon size={18} className={rc.color} />
                    </div>

                    {/* Info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <p className={`font-semibold text-sm text-gray-900 ${!u.aktiv ? 'line-through text-gray-400' : ''}`}>
                          {u.name}
                        </p>
                        {isSelf && (
                          <span className="text-[10px] bg-blue-100 text-blue-600 px-1.5 py-0.5 rounded-full font-medium">Ich</span>
                        )}
                      </div>
                      <p className="text-xs text-gray-500 truncate">{u.email}</p>
                      <div className="flex items-center gap-2 mt-0.5">
                        <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded-full ${rc.bg} ${rc.color}`}>
                          {rc.label}
                        </span>
                        {!u.aktiv && (
                          <span className="text-[10px] font-medium px-1.5 py-0.5 rounded-full bg-gray-100 text-gray-500">
                            Inaktiv
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="flex gap-1.5 shrink-0">
                      <button
                        onClick={() => toggleAktiv(u)}
                        title={u.aktiv ? 'Deaktivieren' : 'Aktivieren'}
                        className={`w-8 h-8 rounded-xl flex items-center justify-center transition-colors ${
                          u.aktiv
                            ? 'bg-amber-50 text-amber-500 active:bg-amber-100'
                            : 'bg-green-50 text-green-500 active:bg-green-100'
                        }`}
                      >
                        {u.aktiv ? <UserX size={15} /> : <UserCheck size={15} />}
                      </button>
                      <button
                        onClick={() => openEdit(u)}
                        className="w-8 h-8 rounded-xl bg-gray-100 text-gray-600 flex items-center justify-center active:bg-gray-200"
                      >
                        <Pencil size={15} />
                      </button>
                      {!isSelf && (
                        <button
                          onClick={() => setDeleteConfirm(u)}
                          className="w-8 h-8 rounded-xl bg-red-50 text-red-500 flex items-center justify-center active:bg-red-100"
                        >
                          <Trash2 size={15} />
                        </button>
                      )}
                    </div>
                  </Card>
                )
              })}
            </div>
          </>
        )}
      </div>

      {/* ── Neuer Benutzer ──────────────────────────────────── */}
      <Modal
        open={showNew}
        onClose={() => setShowNew(false)}
        title="Neuer Benutzer"
        footer={
          <div className="flex gap-2">
            <Button variant="outline" fullWidth onClick={() => setShowNew(false)}>Abbrechen</Button>
            <Button
              fullWidth
              disabled={!isNewValid}
              loading={createMutation.isPending}
              onClick={() => createMutation.mutate(form)}
            >
              Erstellen
            </Button>
          </div>
        }
      >
        <UserForm form={form} set={set} isNew />
        {createMutation.error && (
          <p className="text-sm text-red-600 mt-3">{(createMutation.error as Error).message}</p>
        )}
      </Modal>

      {/* ── Benutzer bearbeiten ─────────────────────────────── */}
      <Modal
        open={!!editUser}
        onClose={() => setEditUser(null)}
        title={editUser?.name ?? 'Bearbeiten'}
        footer={
          <div className="flex gap-2">
            <Button variant="outline" fullWidth onClick={() => setEditUser(null)}>Abbrechen</Button>
            <Button
              fullWidth
              disabled={!isValid}
              loading={updateMutation.isPending}
              onClick={() => editUser && updateMutation.mutate({ ...form, id: editUser.id })}
            >
              Speichern
            </Button>
          </div>
        }
      >
        <UserForm form={form} set={set} isNew={false} />
        {editUser && (
          <p className="text-xs text-gray-400 mt-3">
            Erstellt: {formatDatum(editUser.erstellt_am)}
          </p>
        )}
        {updateMutation.error && (
          <p className="text-sm text-red-600 mt-3">{(updateMutation.error as Error).message}</p>
        )}
      </Modal>

      {/* ── Löschen bestätigen ──────────────────────────────── */}
      <Modal
        open={!!deleteConfirm}
        onClose={() => setDeleteConfirm(null)}
        title="Benutzer löschen?"
        footer={
          <div className="flex gap-2">
            <Button variant="outline" fullWidth onClick={() => setDeleteConfirm(null)}>Abbrechen</Button>
            <Button
              variant="danger" fullWidth
              loading={deleteMutation.isPending}
              onClick={() => deleteConfirm && deleteMutation.mutate(deleteConfirm.id)}
            >
              Löschen
            </Button>
          </div>
        }
      >
        <p className="text-sm text-gray-700">
          Soll <strong>{deleteConfirm?.name}</strong> ({deleteConfirm?.email}) wirklich gelöscht werden?
          Diese Aktion kann nicht rückgängig gemacht werden.
        </p>
        {deleteMutation.error && (
          <p className="text-sm text-red-600 mt-3">{(deleteMutation.error as Error).message}</p>
        )}
      </Modal>
    </>
  )
}

function UserForm({
  form, set, isNew,
}: {
  form: FormState
  set: (k: keyof FormState, v: unknown) => void
  isNew: boolean
}) {
  return (
    <div className="space-y-4">
      <Input
        label="Name"
        required
        value={form.name}
        onChange={(e) => set('name', e.target.value)}
        placeholder="z.B. Marco Müller"
      />
      <Input
        label="E-Mail"
        type="email"
        required
        value={form.email}
        onChange={(e) => set('email', e.target.value)}
        placeholder="marco@vaporex.ch"
      />
      <Select
        label="Rolle"
        value={form.rolle}
        onChange={(e) => set('rolle', e.target.value)}
        options={[
          { value: 'admin',     label: '🔐 Admin — Vollzugriff inkl. Einstellungen' },
          { value: 'alles',     label: '✅ Vollzugriff — alle Module' },
          { value: 'techniker', label: '🔧 Techniker — Service & Geräte' },
          { value: 'verkauf',   label: '📈 Verkauf — Kunden & Offerten' },
        ]}
      />
      <Input
        label={isNew ? 'Passwort' : 'Neues Passwort (leer = unverändert)'}
        type="password"
        required={isNew}
        value={form.passwort}
        onChange={(e) => set('passwort', e.target.value)}
        placeholder={isNew ? 'Mindestens 6 Zeichen' : 'Leer lassen zum Beibehalten'}
      />
      {!isNew && (
        <label className="flex items-center gap-3 py-1 cursor-pointer">
          <div
            onClick={() => set('aktiv', !form.aktiv)}
            className={`w-11 h-6 rounded-full transition-colors ${form.aktiv ? 'bg-blue-600' : 'bg-gray-300'}`}
          >
            <div className={`w-5 h-5 bg-white rounded-full shadow-sm mt-0.5 transition-transform ${form.aktiv ? 'translate-x-5.5' : 'translate-x-0.5'}`} />
          </div>
          <span className="text-sm text-gray-700 font-medium">
            {form.aktiv ? 'Aktiv' : 'Inaktiv'}
          </span>
        </label>
      )}
    </div>
  )
}
